import { useCallback } from 'react'

import { type SortDescriptor } from '@heroui/react'
import { cn, Table } from '@heroui/react'
import { useTranslation } from 'react-i18next'

import { Pagination } from '@/components/ui/Pagination'
import { RowsPerPage } from '@/components/ui/RowsPerPage'
import { useVehiclesStore } from '@/store/vehicles.store.ts'
import type { Vehicle } from '@/types/vehicle.types'

import { VehicleTableRow } from './VehicleTableRow'

interface VehiclesTableProps {
  vehicles: Vehicle[];
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  sortDescriptor: SortDescriptor;
  onSortChange: (descriptor: SortDescriptor) => void;
}

function SortableColumnHeader({
                                 children,
                                 sortDirection,
                               }: {
  children: React.ReactNode;
  sortDirection?: 'ascending' | 'descending';
}) {
  return (
    <span className="flex items-center justify-between gap-1">
      {children}
      {!!sortDirection && (
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          className={cn(
            'transform transition-transform duration-150 ease-out',
            sortDirection === 'descending' ? 'rotate-180' : '',
          )}
        >
          <path d="M7 14l5-5 5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                strokeLinejoin="round"
          />
        </svg>
      )}
    </span>
  )
}

export function VehiclesTable({
                                 onEdit,
                                 onDelete,
                                 sortDescriptor,
                                 onSortChange,
                               }: VehiclesTableProps) {
  const { t } = useTranslation()
  const { vehicles, pagination, setPagination } = useVehiclesStore()

  const handlePageChange = useCallback((pageNumber: number) => {
    setPagination({ page: pageNumber })
  }, [setPagination])

  const handleLimitChange = useCallback((limit: number) => {
    setPagination({ limit, page: 0 })
  }, [setPagination])

  const start = (pagination.page == 0 ? 0 : (pagination.page)) * pagination.limit + 1
  const end = Math.min((pagination.page + 1) * pagination.limit, pagination.total)

  return (
    <Table>
      <Table.ScrollContainer className="max-h-[calc(100vh-theme(spacing.48))]">
        <Table.Content
          aria-label="Vehicles list table"
          sortDescriptor={sortDescriptor}
          onSortChange={onSortChange}
        >
          <Table.Header className="sticky top-0 z-10">
            <Table.Column allowsSorting id="plate" >
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('vehicles.plate')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column allowsSorting id="brand" >
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('vehicles.brand')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column allowsSorting id="year" >
              {({ sortDirection }) => (
                <SortableColumnHeader
                  sortDirection={sortDirection}
                >{t('vehicles.year')}</SortableColumnHeader>
              )}
            </Table.Column>
            <Table.Column id="driverName" >
              {t('reports.driver')}
            </Table.Column>

            <Table.Column></Table.Column>
          </Table.Header>
          <Table.Body>
            {vehicles.map((v: any) => (
              <VehicleTableRow
                key={v.id}
                vehicle={v}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>

      <Table.Footer>
        <div className="flex w-full items-center justify-between ">
          <div className="flex items-center gap-4">
            <RowsPerPage
              value={pagination.limit}
              onChange={handleLimitChange}
            />
            <span className="text-xs text-foreground/50">
              {t('common.pagination_summary', { start, end, total: pagination.total })}
            </span>
          </div>
          <Pagination
            page={pagination.page}
            total={pagination.totalPages}
            onChange={handlePageChange}
          />
        </div>
      </Table.Footer>
    </Table>
  )
}
